import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiPlay as PlayIcon,
  FiCheckCircle as CheckIcon,
  FiXCircle as XIcon,
  FiClock as ClockIcon,
  FiX as CloseIcon,
  FiTrash2 as TrashIcon,
  FiSearch as SearchIcon,
  FiFilter as FilterIcon,
  FiRefreshCw as RefreshIcon,
  FiChevronRight as ChevronRight,
} from 'react-icons/fi';
import { runsAPI, catalogAPI } from '../../../services/dataopsApi';
import { markDataOpsStepComplete } from '../../../hooks/useDataOpsOnboardingStatus';
import Checkbox from '../../../components/common/Checkbox';
import { useProject } from '../../../contexts/ProjectContext';
import { useWorkloads } from '../../hub/workloads/hooks/useWorkloads';
import { useFilter } from '../../../contexts/FilterContext';
import ProjectAssignmentSelect from '../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../components/WorkloadAssignmentSelect';

interface RunsOverview {
  total_runs: number;
  success_rate: number;
  successful_runs: number;
  failed_runs: number;
  running_runs: number;
}

interface PipelineRun {
  id: string;
  status: string;
  job_id?: string | null;
  duration_seconds?: number | null;
  started_at?: string | null;
  completed_at?: string | null;
  error_message?: string | null;
}

type RunStatus = 'success' | 'failed' | 'running' | 'pending';

function getStatusIcon(status: string): React.ReactElement {
  switch (status as RunStatus) {
    case 'success':
      return <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />;
    case 'failed':
      return <XIcon className="w-5 h-5 text-red-600 dark:text-red-400" />;
    case 'running':
      return <ClockIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />;
    default:
      return <ClockIcon className="w-5 h-5 text-gray-400" />;
  }
}

function getStatusColor(status: string): string {
  switch (status as RunStatus) {
    case 'success':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    case 'running':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
    case 'pending':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
}

const RunsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<RunsOverview | null>(null);
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [runDetail, setRunDetail] = useState<PipelineRun | null>(null);
  const [jobName, setJobName] = useState<string | null>(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  const [runDetailError, setRunDetailError] = useState<string | null>(null);
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [jobProjectMap, setJobProjectMap] = useState<Record<string, string | null>>({});
  const [jobWorkloadMap, setJobWorkloadMap] = useState<Record<string, string | null>>({});
  const { selectedProjectId, projects } = useProject();
  const { workloads } = useWorkloads();
  const { filterType, selectedId: filterId } = useFilter();

  // Mark onboarding step as complete when visiting this page
  useEffect(() => {
    markDataOpsStepComplete('monitor_runs');
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, selectedProjectId, filterType, filterId]);

  useEffect(() => {
    if (!viewModalOpen || !selectedRunId) return;
    setRunDetail(null);
    setJobName(null);
    setRunDetailError(null);
    setRunDetailLoading(true);
    runsAPI
      .getRun(selectedRunId)
      .then((run: PipelineRun) => {
        setRunDetail(run);
        if (run.job_id) {
          return catalogAPI
            .getJob(run.job_id)
            .then((job: { name?: string } | null) => setJobName(job?.name ?? null))
            .catch(() => {});
        }
      })
      .catch((err: unknown) =>
        setRunDetailError(
          err instanceof Error ? err.message : 'Failed to load run details'
        )
      )
      .finally(() => setRunDetailLoading(false));
  }, [viewModalOpen, selectedRunId]);

  const getProjectName = (projectId: string | null | undefined): string | null => {
    if (!projectId) return null;
    return projects.find(p => p.id === projectId)?.name ?? null;
  };

  const getWorkloadName = (workloadId: string | null | undefined): string | null => {
    if (!workloadId) return null;
    return workloads.find(w => w.id === workloadId)?.name ?? null;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, runsData, jobsData] = await Promise.all([
        runsAPI.getOverview(),
        runsAPI.getRuns({ status: statusFilter === 'all' ? undefined : statusFilter }),
        catalogAPI.getJobs(),
      ]);
      setOverview(overviewData);
      setRuns(runsData);
      const pMap: Record<string, string | null> = {};
      const wMap: Record<string, string | null> = {};
      for (const job of jobsData) {
        pMap[job.id] = job.project_id ?? null;
        wMap[job.id] = job.workload_id ?? null;
      }
      setJobProjectMap(pMap);
      setJobWorkloadMap(wMap);
    } catch (err: unknown) {
      console.error('Error loading runs data:', err instanceof Error ? err.message : err);
    } finally {
      setLoading(false);
    }
  };

  const openViewRun = (id: string) => {
    setSelectedRunId(id);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedRunId(null);
    setRunDetail(null);
    setJobName(null);
    setRunDetailError(null);
  };

  const handleRemoveRun = async (runId: string) => {
    if (!globalThis.confirm('Remove this run? It will be excluded from the list and metrics.')) return;
    try {
      await runsAPI.removeRun(runId);
      loadData();
    } catch (err: unknown) {
      console.error('Error removing run:', err instanceof Error ? err.message : err);
    }
  };

  const handleBulkRemove = async () => {
    if (selectedRunIds.size === 0) return;
    const count = selectedRunIds.size;
    if (!globalThis.confirm(`Remove ${count} run${count > 1 ? 's' : ''}? They will be excluded from the list and metrics.`)) return;
    try {
      await Promise.all(Array.from(selectedRunIds).map((id) => runsAPI.removeRun(id)));
      setSelectedRunIds(new Set());
      loadData();
    } catch (err: unknown) {
      console.error('Error removing runs:', err instanceof Error ? err.message : err);
    }
  };

  const toggleSelectRun = useCallback((runId: string) => {
    setSelectedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  }, []);

  const filteredRuns = useMemo(() => {
    if (!searchQuery.trim()) return runs;
    const q = searchQuery.toLowerCase();
    return runs.filter(
      (r) =>
        r.job_id?.toLowerCase().includes(q) ||
        r.error_message?.toLowerCase().includes(q)
    );
  }, [runs, searchQuery]);

  const toggleSelectAll = useCallback(() => {
    setSelectedRunIds((prev) =>
      prev.size === filteredRuns.length ? new Set() : new Set(filteredRuns.map((r) => r.id))
    );
  }, [filteredRuns]);

  if (loading) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <PlayIcon className="w-12 h-12 text-blue-600 dark:text-blue-400 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Pipeline Runs</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">Monitor and track pipeline executions</p>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <PlayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Runs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.total_runs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <CheckIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{overview?.success_rate?.toFixed(1) || 0}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <CheckIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Successful</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.successful_runs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <XIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{overview?.failed_runs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Running</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{overview?.running_runs || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={filteredRuns.length > 0 && selectedRunIds.size === filteredRuns.length}
                onChange={toggleSelectAll}
              />
            </div>
            <div className="flex items-center gap-2">
              <FilterIcon className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search runs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
              <option value="pending">Pending</option>
            </select>
            <button
              type="button"
              onClick={loadData}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedRunIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {selectedRunIds.size} run{selectedRunIds.size > 1 ? 's' : ''} selected
            </span>
            <button
              type="button"
              onClick={() => setSelectedRunIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              currentProjectId={null}
              onAssign={async (projectId) => {
                const jobIds = Array.from(new Set(
                  runs
                    .filter(r => selectedRunIds.has(r.id) && r.job_id)
                    .map(r => r.job_id as string)
                ));
                if (jobIds.length > 0) {
                  await catalogAPI.bulkAssignProject(jobIds, 'job', projectId);
                  loadData();
                }
                setSelectedRunIds(new Set());
              }}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              currentWorkloadId={null}
              onAssign={async (workloadId) => {
                const jobIds = Array.from(new Set(
                  runs
                    .filter(r => selectedRunIds.has(r.id) && r.job_id)
                    .map(r => r.job_id as string)
                ));
                if (jobIds.length > 0) {
                  await catalogAPI.bulkAssignWorkload(jobIds, 'job', workloadId);
                  loadData();
                }
                setSelectedRunIds(new Set());
              }}
              size="sm"
            />
            <button
              type="button"
              onClick={handleBulkRemove}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Runs List (card-style rows) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredRuns.map((run) => (
            <div
              key={run.id}
              className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              onClick={() => openViewRun(run.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openViewRun(run.id); }} role="button" tabIndex={0}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
                    <Checkbox
                      checked={selectedRunIds.has(run.id)}
                      onChange={() => toggleSelectRun(run.id)}
                    />
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(run.status)}`}>
                    {getStatusIcon(run.status)}
                    {run.status}
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Job {run.job_id}
                  </span>
                  {run.job_id && getProjectName(jobProjectMap[run.job_id]) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                      {getProjectName(jobProjectMap[run.job_id])}
                    </span>
                  )}
                  {run.job_id && getWorkloadName(jobWorkloadMap[run.job_id]) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                      {getWorkloadName(jobWorkloadMap[run.job_id])}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <ClockIcon className="w-3 h-3" />
                    {formatDuration(run.duration_seconds)}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {run.started_at ? new Date(run.started_at).toLocaleString() : '-'}
                  </span>
                  {run.error_message && (
                    <span className="text-sm text-red-600 dark:text-red-400 truncate max-w-[200px]">
                      {run.error_message}
                    </span>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
              </div>
            </div>
          ))}
          {filteredRuns.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
              No runs found
            </div>
          )}
        </div>
      </div>

      {/* View run detail modal */}
      {viewModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
              onClick={closeViewModal}
              onKeyDown={(e) => e.key === 'Escape' && closeViewModal()}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Run details</h3>
                  <button
                    type="button"
                    onClick={closeViewModal}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                {runDetailLoading && (
                  <div className="py-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>
                )}
                {runDetailError && (
                  <div className="py-4 text-red-600 dark:text-red-400 text-sm">{runDetailError}</div>
                )}
                {!runDetailLoading && !runDetailError && runDetail && (
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Status</dt>
                      <dd className="mt-0.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(runDetail.status)}`}>
                          {runDetail.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Job</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">
                        {jobName || runDetail.job_id}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Started</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">
                        {runDetail.started_at ? new Date(runDetail.started_at).toLocaleString() : '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Completed</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">
                        {runDetail.completed_at ? new Date(runDetail.completed_at).toLocaleString() : '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-gray-500 dark:text-gray-400">Duration</dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-white">
                        {formatDuration(runDetail.duration_seconds)}
                      </dd>
                    </div>
                    {runDetail.error_message && (
                      <div>
                        <dt className="font-medium text-gray-500 dark:text-gray-400">Error</dt>
                        <dd className="mt-0.5 text-red-600 dark:text-red-400">{runDetail.error_message}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>
              {!runDetailLoading && !runDetailError && runDetail && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      handleRemoveRun(runDetail.id);
                      closeViewModal();
                    }}
                    className="text-sm text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                  >
                    Delete Run
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunsPage;
